import { ThreadListItemPrimitive, useAuiState } from '@assistant-ui/react';
import { Archive, ArchiveRestore } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSessionRow, useSetSessionTitle } from './queries';

/**
 * One active conversation in the sidebar (shown once it has a first message): its title (AI-generated after the
 * first message, up to two lines; click to edit, saved on blur) above the
 * first-message preview (up to 250 characters, wrapped). Clicking the preview opens the conversation. Archive asks first,
 * and is hidden while the title is being edited.
 */
export function SessionCard() {
  const remoteId = useAuiState((s) => s.threadListItem.remoteId);
  const { data: row } = useSessionRow(remoteId);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // A new conversation joins the list only once sreg-api has its first
  // message: it appears when Simon's first answer finishes (the session list
  // refreshes then), already with its preview and generated title, instead
  // of as an empty "Title / New conversation" card (Austin, 2026-10-24).
  if (!row?.firstUserMessage) return null;

  return (
    <ThreadListItemPrimitive.Root className="group relative mb-1 rounded-lg border border-transparent px-2 py-2 hover:bg-surface data-[active]:border-line data-[active]:bg-surface">
      <TitleInput vertexSessionId={row?.vertexSessionId} saved={row?.title ?? ''} onEditingChange={setEditing} />
      <ThreadListItemPrimitive.Trigger className="mt-0.5 block w-full whitespace-normal break-words text-left text-xs leading-snug text-ink-soft">
        {row.firstUserMessage}
      </ThreadListItemPrimitive.Trigger>
      {!editing && row && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className={`absolute right-2 top-2.5 rounded p-1 text-ink-faint hover:text-ink ${confirming ? 'block' : 'hidden group-hover:block'}`}
          aria-label="Archive conversation"
          title="Archive"
        >
          <Archive size={14} />
        </button>
      )}
      {row && <div className="mt-1 text-right text-[10px] tabular-nums text-ink-faint">{cardTime(row.lastActivityAt ?? row.createdAt)}</div>}
      {confirming && <ConfirmArchive onCancel={() => setConfirming(false)} />}
    </ThreadListItemPrimitive.Root>
  );
}

/** DD/MM/YY HH:MM in the browser's timezone (Austin, 2026-10-08). */
export function cardTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${p(d.getFullYear() % 100)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function ConfirmArchive({ onCancel }: { onCancel: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[data-cancel]')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && onCancel();
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onCancel]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Archive this conversation?"
      className="absolute right-1 top-9 z-20 w-56 rounded-xl border border-line bg-surface p-3 shadow-lg"
    >
      <p className="text-sm font-semibold text-ink">Archive this conversation?</p>
      <p className="mt-1 text-xs text-ink-soft">It moves to Archived. You can restore it any time.</p>
      <div className="mt-3 flex justify-end gap-2">
        <button
          data-cancel
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken"
        >
          Cancel
        </button>
        <ThreadListItemPrimitive.Archive className="rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-strong">
          Archive
        </ThreadListItemPrimitive.Archive>
      </div>
    </div>
  );
}

function TitleInput({
  vertexSessionId,
  saved,
  onEditingChange,
}: {
  vertexSessionId?: string;
  saved: string;
  onEditingChange: (editing: boolean) => void;
}) {
  const [value, setValue] = useState(saved);
  const [editing, setEditing] = useState(false);
  const save = useSetSessionTitle();
  useEffect(() => setValue(saved), [saved]);

  const startEditing = () => {
    if (!vertexSessionId) return; // a brand-new conversation has no sreg-api row until its first message
    setEditing(true);
    onEditingChange(true);
  };

  const commit = () => {
    setEditing(false);
    onEditingChange(false);
    const next = value.trim();
    if (!vertexSessionId || next === saved) return;
    save.mutate({ vertexSessionId, title: next });
  };

  if (!editing) {
    // Up to two lines, then an ellipsis (Austin, 2026-10-23).
    return (
      <button
        type="button"
        onClick={startEditing}
        disabled={!vertexSessionId}
        title={saved || undefined}
        aria-label="Conversation title"
        className="line-clamp-2 block w-full break-words pr-7 text-left text-sm font-semibold leading-snug text-ink disabled:cursor-default"
      >
        {saved || <span className="font-normal text-ink-faint">Title</span>}
      </button>
    );
  }

  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValue(saved);
          e.currentTarget.blur();
        }
      }}
      maxLength={250}
      placeholder="Title"
      aria-label="Conversation title"
      className="w-full rounded bg-surface-sunken px-1 text-sm font-semibold text-ink outline-none placeholder:font-normal placeholder:text-ink-faint"
    />
  );
}

/** An archived conversation: read-only label, Restore. */
export function ArchivedCard() {
  const remoteId = useAuiState((s) => s.threadListItem.remoteId);
  const { data: row } = useSessionRow(remoteId);
  return (
    <ThreadListItemPrimitive.Root className="group mb-1 flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-surface">
      <ThreadListItemPrimitive.Trigger className="min-w-0 flex-1 whitespace-normal break-words text-left text-xs leading-snug text-ink-faint">
        {row?.title ? <span className="font-semibold text-ink-soft">{row.title} · </span> : null}
        {row?.firstUserMessage || 'Conversation'}
      </ThreadListItemPrimitive.Trigger>
      <ThreadListItemPrimitive.Unarchive
        className="shrink-0 rounded p-1 text-ink-faint hover:text-sage"
        aria-label="Restore conversation"
        title="Restore"
      >
        <ArchiveRestore size={14} />
      </ThreadListItemPrimitive.Unarchive>
    </ThreadListItemPrimitive.Root>
  );
}
